import { useCallback, useEffect, useState } from "react";
import { Star } from "lucide-react";
import { apiFetch } from "@/api";
import ReviewPrompt from "@/components/ReviewPrompt";

const HOME_REVIEW_BOX_KEY = "abhyas_home_review_box_dismissed";

/* Testimonials — compact horizontal marquee strip fed by REAL reviews from
   /api/reviews/public. Continuous right→left seamless loop (content duplicated),
   pauses on hover, no controls. Review-submission prompt preserved below. */

const MarqueeCard = ({ review }) => (
  <figure className="flex w-[300px] shrink-0 flex-col justify-between rounded-2xl border border-[#14532D]/10 bg-white p-5 shadow-[0_6px_20px_-10px_rgba(20,83,45,0.08)] sm:w-[340px]">
    <div>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-3.5 w-3.5 ${
              star <= review.rating
                ? "fill-[#FACC15] text-[#FACC15]"
                : "fill-[#E2E8F0] text-[#E2E8F0]"
            }`}
          />
        ))}
      </div>
      {review.reviewText && (
        <blockquote className="mt-3 line-clamp-3 text-sm font-medium leading-relaxed text-[#17201A] sm:text-[15px]">
          “{review.reviewText}”
        </blockquote>
      )}
    </div>
    <figcaption className="mt-4 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#647067]">
      {review.name} · Student
    </figcaption>
  </figure>
);

const TestimonialsSection = () => {
  const [data, setData] = useState(null);
  const [boxVisible, setBoxVisible] = useState(
    () => !localStorage.getItem(HOME_REVIEW_BOX_KEY)
  );

  const load = useCallback(() => {
    apiFetch("/reviews/public").then(setData).catch(() => {});
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const closeBox = () => {
    localStorage.setItem(HOME_REVIEW_BOX_KEY, "1");
    setBoxVisible(false);
    load(); // pick up the just-submitted review immediately
  };

  const reviews = data?.reviews ?? [];
  if (!boxVisible && (!data || !reviews.length)) return null;

  // Duplicate the sequence until it can loop seamlessly (two identical halves,
  // each at least as wide as the viewport strip)
  let items = [...reviews];
  while (items.length > 0 && items.length < 8) items = [...items, ...items];
  const track = [...items, ...items];
  const duration = Math.max(30, items.length * 6);

  return (
    <section
      data-testid="testimonials-section"
      className="overflow-hidden py-16 sm:py-20"
    >
      <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
        <p className="mb-3 flex justify-center">
          <span className="inline-flex items-center gap-2 rounded-lg border border-[#16A34A]/15 bg-[#16A34A]/8 px-3.5 py-1.5 text-[13px] font-bold uppercase tracking-[0.08em] text-[#14532D] sm:text-[14px] lg:text-[15px]">
            <span className="pulse-dot inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#16A34A]" />
            Testimonials
          </span>
        </p>
        <h2
          data-testid="testimonials-headline"
          className="font-display text-[36px] font-bold leading-[1.15] tracking-tight text-[#17201A] sm:text-[48px] lg:text-[56px]"
        >
          What students say about{" "}
          <span className="text-[#16A34A]">preparing with Abhyas.</span>
        </h2>
      </div>

      {reviews.length > 0 && (
        <div
          data-testid="testimonial-marquee-container"
          className="group relative mt-12 overflow-hidden"
          style={{
            maskImage:
              "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
            WebkitMaskImage:
              "linear-gradient(to right, transparent, black 8%, black 92%, transparent)",
          }}
        >
          <div
            data-testid="testimonial-marquee-track"
            className="flex w-max gap-5 py-2 group-hover:[animation-play-state:paused]"
            style={{ animation: `marquee ${duration}s linear infinite` }}
          >
            {track.map((review, i) => (
              <MarqueeCard key={`${review.user_id || review.name}-${i}`} review={review} />
            ))}
          </div>
        </div>
      )}

      {boxVisible && (
        <div
          data-testid="testimonial-review-prompt"
          className="mx-auto mt-10 max-w-md px-4"
        >
          <ReviewPrompt onClose={closeBox} />
        </div>
      )}
    </section>
  );
};

export default TestimonialsSection;
